import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronLeft, faMinus } from '@fortawesome/pro-regular-svg-icons';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import type { MetadataProps } from '~/types/Metadata';
import { searchQueryFromUrl } from '~/helpers/projectConstant';
import LayoutWrapper from '~/layouts/LayoutWrapper';
import Breadcrumbs from '~/components/Breadcrumbs';
import Container from '~/components/ui/Container';
import Panel from '~/components/ui/Panel';
import { Link } from '~/components/ui/Link';
import data_checked from '~/assets/images/data-checked.svg';

interface Params {
  productgroep?: string;
  [key: string]: string | undefined;
}

interface SearchExplanationPageProps extends MetadataProps {
  productGroup: string;
  geoLocation: string;
}

export const SearchExplanationPage: React.FC<SearchExplanationPageProps> = ({ 
  productGroup,
  geoLocation,
  campaign
  }) => {  
  const breadcrumbLinks = [
    { name: 'Home', linkTo: '/' },
    { name: 'Zoeken', linkTo: `/${productGroup}/zoekresultaat${searchQueryFromUrl}` },
    { name: 'Hoe werkt het zoekresultaat', linkTo: '' },
  ];

  return (
    <LayoutWrapper
      campaign={campaign}
      breadcrumbsComponent={
        <Breadcrumbs links={breadcrumbLinks} campaign={campaign} />
      }
    >
      <Container state="defaultLayoutFirst">
        <div className="flex mb-10 md:w-2/3 justify-between">
          <div></div>
          <div className="hidden lg:flex align-right ">
            <Link
              title="Ga terug naar het zoekresultaat"
              href={`/${productGroup}/zoekresultaat${searchQueryFromUrl}`}
              variant="default"
              className="bg-stone-100 border-stone-200 md:text-sm font-bold"
            >
              <FontAwesomeIcon icon={faChevronLeft as IconProp} />
              Ga terug naar het zoekresultaat
            </Link>
          </div>
        </div>
        <Panel className="w-full md:w-2/3 space-y-3 md:space-y-6" position="last">
          <h1
            className="font-avantgarde font-bold text-blue-900
      text-[22px] md:text-[32px] leading-[38px] md:leading-[48px]"
          >
            Hoe werkt het zoekresultaat
          </h1>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            Advieskeuze helpt je bij het vinden van een gespecialiseerde
            dienstverlener bij jouw in de buurt. Ruim 16.000 dienstverleners
            hebben een vermelding op Advieskeuze. Hieronder leggen we uit hoe
            je met de zoekfuncties (filtering en sortering) de dienstverlener kunt
            vinden waar jij naar op zoek bent.
          </p>
          <div className="h-[2px] bg-stone-100"></div>
          <h2
            className="font-avantgarde font-bold text-blue-700
      text-[22px] md:text-[26px] leading-[33px] md:leading-[39px]"
          >
            Zoeken op locatie
          </h2>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            De meest gebruikte sortering is op locatie. Je vult dan je plaatsnaam
            of postcode in en er wordt een zoekresultaat van kantoren getoond.
            Indien je zoekt op postcode dan worden de kantoren getoond die binnen
            een straal van X km liggen aangevuld met de overige kantoren in de
            betreffende plaats. Indien je zoekt op plaatsnaam dan worden alle
            kantoren in de betreffende plaats getoond. Zoek je bijvoorbeeld in
            ‘Amsterdam’ of een postcode van Amsterdam binnen een straal van 5 km
            dan worden alle kantoren binnen Amsterdam getoond, ook als ze buiten
            de straal van 5 km liggen.
          </p>
          <div className="h-[2px] bg-stone-100"></div>
          <h2
            className="font-avantgarde font-bold text-blue-700
      text-[22px] md:text-[26px] leading-[33px] md:leading-[39px]"
          >
            Hoe zijn de dienstverleners gesorteerd?
          </h2>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            Bovenaan de zoekresultaten zie je standaard de organisatie die de
            meest recente review van het onderwerp van het zoekresultaat heeft
            ontvangen. Zoek je op hypotheekadviseur, dan sorteert het resultaat op
            de meest recente reviewdatum van hypotheekadvies. Zoek je op
            pensioenadvies, dan geldt de laatste pensioenreview etc. Bovenaan de
            zoekresultaten kun je een andere sortering selecteren. Er zijn vier
            sorteringen mogelijk:
          </p>
          <ul className="space-y-[12px]">
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <div
                className="font-body text-stone-800
                text-[16px]  leading-[26.4px]"
              >
                <span className="font-bold">Laatste review (standaard): </span>
                <span className="font-normal">
              sorteert op de datum van de meest recente review van een
              dienstverlener (binnen de scope van het zoekresultaat).
            </span>
              </div>
            </li>
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <div
                className="font-body text-stone-800
                text-[16px]  leading-[26.4px]"
              >
                <span className="font-bold">Reviews: </span>
                <span className="font-normal">
              sorteert op het aantal reviews voor het gezochte product
              (bijvoorbeeld hypotheekadvies).
            </span>
              </div>
            </li>
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <div
                className="font-body text-stone-800
                text-[16px]  leading-[26.4px]"
              >
                <span className="font-bold">Afstand: </span>
                <span className="font-normal">
              sorteert op absolute afstand tussen de geografische zoekopdracht
              en de dienstverlener.
            </span>
              </div>
            </li>
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <div
                className="font-body text-stone-800
                text-[16px]  leading-[26.4px]"
              >
                <span className="font-bold">Naam: </span>
                <span className="font-normal">
              sorteert op naam (alfabetische volgorde) van de dienstverlener.
            </span>
              </div>
            </li>
          </ul>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            Het zoekresultaat van Advieskeuze toont maximaal 200 bedrijven in
            de omgeving van jouw zoekopdracht. Je kunt het aantal zoekresultaten
            terugbrengen door één of meer filters toe te passen. De filters
            verschillen per productgroep.
          </p>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            Bij financiële producten staat de filter ‘Alleen gecontroleerde data’
            standaard aangevinkt. Dit betekent dat het zoekresultaat alleen de
            kantoren toont die hun gegevens op Advieskeuze door ons hebben
            laten controleren (zie ook ‘Wat betekent het logo ‘Gecheckt door
            Advieskeuze?’). Als je deze filter uitvinkt zie je alle kantoren in
            jouw omgeving.
          </p>
          <div className="h-[2px] bg-stone-100"></div>
          <h2
            className="font-avantgarde font-bold text-blue-700
      text-[22px] md:text-[26px] leading-[33px] md:leading-[39px]"
          >
            Hoe werken de filters?
          </h2>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            Het zoekresultaat van Advieskeuze toont maximaal 200 bedrijven in
            de omgeving van jouw zoekopdracht. Je kunt het aantal zoekresultaten
            terugbrengen door één of meer filters toe te passen. De filters
            verschillen per productgroep.
          </p>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            Bij financiële producten staat de filter ‘Alleen gecontroleerde data’
            standaard aangevinkt. Dit betekent dat het zoekresultaat alleen de
            kantoren toont die hun gegevens op Advieskeuze door ons hebben
            laten controleren (zie ook ‘Wat betekent het logo ‘Gecheckt door
            Advieskeuze’?). Als je deze filter uitvinkt zie je alle kantoren in
            jouw omgeving.
          </p>
          <div className="h-[2px] bg-stone-100"></div>
          <h2
            className="font-avantgarde font-bold text-blue-700
      text-[22px] md:text-[26px] leading-[33px] md:leading-[39px]"
          >
            Wat betekent het logo ‘Gecheckt door Advieskeuze’?
          </h2>
          <div className="flex flex-col md:flex-row gap-[31.4px] pb-[28px]">
            <p
              className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px] w-full md:w-[70%]"
            >
              Dienstverleners kunnen hun profielpagina op Advieskeuze zelf
              beheren en vullen met informatie. Kantoren kunnen er vervolgens voor
              kiezen deze gegevens door ons te laten controleren op
              betrouwbaarheid en actualiteit. Deze kantoren krijgen het ‘Gecheckt
              door Advieskeuze’-stempel. Dit stempel verzekert jou ervan dat de
              informatie die jij ziet op de profielpagina (gegevens en tarieven)
              klopt. Kantoren betalen €200 per jaar voor deze dienst.
            </p>
            <img
              src={data_checked as string}
              alt="Gecheckt door Advieskeuze"
              data-testid="check-badge-image"
              className="w-[151px] h-[151px] self-center object-cover"
            />
          </div>
          <div className="h-[2px] bg-stone-100"></div>
          <h2
            className="font-avantgarde font-bold text-blue-700
      text-[22px] md:text-[26px] leading-[33px] md:leading-[39px]"
          >
            Hoe krijgen kantoren een score
          </h2>
          <p
            className="font-body font-normal text-stone-800
      text-[16px]  leading-[26.4px]"
          >
            Als je doorklikt op een profielpagina zie je dat sommige
            dienstverleners een score hebben en anderen niet. Wij berekenen de
            score als volgt:
          </p>
          <ul className="space-y-[12px]">
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <span
                className="font-body text-stone-800
                text-[16px] font-normal leading-[26.4px]"
              >
                Een dienstverlener krijgt een score als er tenminste
                <span className="font-bold"> 1 goedgekeurde review </span> bekend
                is in de afgelopen
                <span className="font-bold"> 24 maanden.</span>
              </span>
            </li>
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <span
                className="font-body text-stone-800
                text-[16px] font-normal leading-[26.4px]"
              >
                Een oude review telt minder zwaar mee in de score dan een recente
                review.
              </span>
            </li>
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <span
                className="font-body text-stone-800
                text-[16px] font-normal leading-[26.4px]"
              >
                De score is een gemiddelde van alles goedgekeurde reviews van een
                dienstverlener (waarbij we rekening houden met de afwaardering
                door tijd).
              </span>
            </li>
            <li className="flex gap-[14px]">
              <FontAwesomeIcon
                icon={faMinus as IconProp}
                className="text-stone-300 text-[14px] mt-[5px]"
              />
              <span
                className="font-body text-stone-800
                text-[16px] leading-[26.4px] font-normal"
              >
                De 5-puntsschaal van de review vertalen we naar een cijfer van 2
                t/m 10.
              </span>
            </li>
          </ul>
        </Panel>
      </Container>
    </LayoutWrapper>
  );
}
